import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import config from 'config/app.config'
import {
  getOnramperThemeParams,
  isAppDarkMode,
  toOnramperCryptoId
} from 'constants/onramperTheme'
import { setOnramperUrl, setShowCashierPopup } from 'redux-thunk/redux/slices/settings.slice'

export const useOnramper = () => {
  const dispatch = useDispatch()
  const { user } = useSelector(state => state.user)
  const { allCurrencies } = useSelector(state => state.gameSetting)
  const { onramperUrl } = useSelector(state => state.settings)
  const [walletAddresses, setWalletAddresses] = useState({})
  const [isDarkTheme, setIsDarkTheme] = useState(() => isAppDarkMode())

  useEffect(() => {
    const activeCurrencyCodes = new Set(
      (allCurrencies || [])
        .filter((currency) => currency.isActive !== false)
        .map((currency) => String(currency.code || '').toUpperCase())
    )
    const addresses = (user?.wallets || []).reduce((acc, wallet) => {
      if (wallet?.walletAddress && wallet?.currency?.code) {
        const code = wallet.currency.code
        if (
          wallet.currency.isActive === false ||
          !activeCurrencyCodes.has(code.toUpperCase())
        ) return acc
        const onramperId = toOnramperCryptoId(code)
        if (!onramperId) return acc
        acc[code.toLowerCase()] = wallet.walletAddress
        if (!acc[onramperId]) acc[onramperId] = wallet.walletAddress
      }
      return acc
    }, {})
    setWalletAddresses(addresses)
  }, [user, allCurrencies])

  useEffect(() => {
    if (typeof document === 'undefined') return undefined

    const syncTheme = () => setIsDarkTheme(isAppDarkMode())
    syncTheme()

    const observer = new MutationObserver(syncTheme)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme'] })
    observer.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-theme'] })

    const media = window.matchMedia?.('(prefers-color-scheme: dark)')
    media?.addEventListener?.('change', syncTheme)

    return () => {
      observer.disconnect()
      media?.removeEventListener?.('change', syncTheme)
    }
  }, [])

  const showOnramperWidget = useCallback(async (currencyCode) => {
    const apiKey = config.ONRAMPER_API_KEY
    if (!apiKey || apiKey === 'undefined') {
      console.warn('Onramper API key is missing')
    }

    const isTestKey = apiKey?.startsWith('pk_test_')
    const baseUrl = isTestKey ? 'https://buy.onramper.dev' : 'https://buy.onramper.com'

    const dark = isAppDarkMode()
    setIsDarkTheme(dark)

    const url = new URL(baseUrl)
    url.searchParams.set('apiKey', apiKey || '')
    url.searchParams.set('mode', 'buy')

    Object.entries(getOnramperThemeParams()).forEach(([key, value]) => {
      url.searchParams.set(key, value)
    })

    const selectableCryptos = [...new Set(
      (allCurrencies || [])
        .filter((currency) => currency.isActive !== false && currency.isFiat === false)
        .map((currency) => toOnramperCryptoId(currency.code))
        .filter(Boolean)
    )]

    if (!selectableCryptos.length) {
      console.warn('No active currencies supported by the Onramper widget')
      return
    }

    const requestedDefaultCrypto = toOnramperCryptoId(currencyCode)
    const defaultCrypto = selectableCryptos.includes(requestedDefaultCrypto)
      ? requestedDefaultCrypto
      : selectableCryptos[0]

    const wallets = selectableCryptos
      .map((id) => (walletAddresses[id] ? `${id}:${walletAddresses[id]}` : null))
      .filter(Boolean)
      .join(',')

      url.searchParams.set('defaultCrypto', defaultCrypto)
      url.searchParams.set('onlyCryptos', selectableCryptos.join(','))
      url.searchParams.set('popularCryptos', selectableCryptos.join(','))
      if (wallets) {
        url.searchParams.set('wallets', wallets)
      }

    url.searchParams.set('defaultFiat', 'USD')
    url.searchParams.set('defaultPaymentMethod', 'creditcard')
    url.searchParams.set('excludePaymentMethods', 'debitcard')

    let widgetUrl = url.toString()
    let finalUrl = widgetUrl

    if (wallets) {
      try {
        const backendUrl = config.apiGateways.BASE_URL_1
        const response = await fetch(`${backendUrl}/onramper/signature`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ widgetUrl })
        })

        const data = await response.json()
        if (data?.data?.signedUrl) {
          finalUrl = data.data.signedUrl
        }
      } catch (err) {
        console.warn('Failed to sign Onramper URL, using unsigned URL:', err)
      }
    }

    dispatch(setOnramperUrl(finalUrl))
    dispatch(setShowCashierPopup(false))
  }, [allCurrencies, walletAddresses, dispatch])

  const closeOnramperWidget = useCallback(() => {
    dispatch(setOnramperUrl(null))
  }, [dispatch])

  return {
    showOnramperWidget,
    onramperUrl,
    closeOnramperWidget,
    isDarkTheme
  }
}
